import React, { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Key, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "sonner";

const formatErr = (d) => {
  if (!d) return "Erreur";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ") || "Erreur";
  return d?.msg || "Erreur";
};

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (password !== confirm) {
      toast.error("Les mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        token,
        new_password: password,
      });
      if (data?.access_token) {
        localStorage.setItem("access_token", data.access_token);
      }
      toast.success("Mot de passe réinitialisé — vous êtes connecté.");
      // Force reload so AuthContext picks up the new token cleanly
      window.location.href = "/";
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="reset-password-page"
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: "#F9F8F6" }}
    >
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo-yacoclean.jpg" alt="YACOCLEAN" data-testid="reset-logo" className="w-24 h-24 rounded-2xl object-cover mb-4 shadow-sm" />
          <h1 className="font-heading text-3xl font-bold tracking-tight">YACOCLEAN</h1>
          <p className="text-sm text-[#5C665E] mt-1">Suivi de factures</p>
        </div>

        <div className="rounded-2xl border bg-white p-8" style={{ borderColor: "#E5E2DC" }}>
          <h2 className="font-heading text-xl font-semibold mb-1">Nouveau mot de passe</h2>
          <p className="text-sm text-[#5C665E] mb-6">
            Choisissez un mot de passe d&apos;au moins 8 caractères.
          </p>

          {!token ? (
            <div
              className="rounded-lg p-4 text-sm"
              style={{ backgroundColor: "#FCE8E8", color: "#B3261E" }}
              data-testid="reset-invalid-token"
            >
              Lien invalide. Demandez un nouveau lien depuis la page « Mot de passe oublié ».
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block">
                  Nouveau mot de passe
                </Label>
                <div className="relative">
                  <Input
                    data-testid="reset-new-password-input"
                    type={showPw ? "text" : "password"}
                    required
                    autoFocus
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="bg-white border-[#E5E2DC] h-11 pr-10"
                  />
                  <button
                    type="button"
                    data-testid="toggle-reset-password-visibility"
                    onClick={() => setShowPw((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C665E] hover:text-[#1A1D1A]"
                    tabIndex={-1}
                  >
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block">
                  Confirmer
                </Label>
                <Input
                  data-testid="reset-confirm-password-input"
                  type={showPw ? "text" : "password"}
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="••••••••"
                  className="bg-white border-[#E5E2DC] h-11"
                />
              </div>

              <button
                type="submit"
                data-testid="reset-password-submit"
                disabled={loading}
                className="w-full rounded-full py-3 text-sm font-semibold text-white btn-lift focus-brand flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ backgroundColor: "#2C5545" }}
              >
                <Key size={16} />
                {loading ? "Réinitialisation..." : "Définir mon nouveau mot de passe"}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-sm text-[#5C665E] hover:text-[#1A1D1A]"
              data-testid="back-to-login-link"
            >
              <ArrowLeft size={14} /> Retour à la connexion
            </Link>
          </div>
        </div>
      </div>
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}
